import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Box, Button, Typography } from '@mui/material'
import DesktopWindowsIcon from '@mui/icons-material/DesktopWindows'
import { ADMIN_MIN_WIDTH } from '@/theme/breakpoints'

/**
 * Shown instead of the admin panel below `md`.
 *
 * Deliberately not dismissible: dismissing it would drop the user into a shell of
 * wide data tables and content editors that cannot be operated by touch, which is
 * exactly the outcome the block exists to prevent. The route, the API and
 * [RequireAdminRole] are untouched: this is a usability gate, not authorization.
 */
export function AdminUnavailableNotice() {
  const { t } = useTranslation()
  const [width, setWidth] = useState(() => window.innerWidth)

  // The reported width is what turns "the admin panel does not work" into an
  // actionable support report, so it has to track resizes and rotation.
  useEffect(() => {
    const onResize = () => setWidth(window.innerWidth)
    window.addEventListener('resize', onResize)
    window.addEventListener('orientationchange', onResize)
    return () => {
      window.removeEventListener('resize', onResize)
      window.removeEventListener('orientationchange', onResize)
    }
  }, [])

  return (
    <Box
      role="alert"
      sx={{
        minHeight: '100dvh',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        gap: 2,
        px: 3,
        py: 6,
        textAlign: 'center',
        backgroundColor: 'var(--color-bg-page)',
      }}
    >
      <DesktopWindowsIcon sx={{ fontSize: 48, color: 'var(--color-muted-text)' }} />

      <Typography variant="h6" component="h1" sx={{ fontWeight: 700, color: 'var(--color-text-strong)' }}>
        {t('adminUnavailable.title')}
      </Typography>

      <Typography variant="body2" sx={{ maxWidth: 460, color: 'var(--color-text-tertiary)' }}>
        {t('adminUnavailable.body')}
      </Typography>

      <Typography variant="caption" sx={{ color: 'var(--color-muted-text)' }}>
        {t('adminUnavailable.width', { width, min: ADMIN_MIN_WIDTH })}
      </Typography>

      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, justifyContent: 'center', mt: 1 }}>
        <Button variant="contained" href="/">
          {t('adminUnavailable.backToViewer')}
        </Button>
        <Button variant="outlined" href={t('links.support')} target="_blank" rel="noopener noreferrer">
          {t('adminUnavailable.contactSupport')}
        </Button>
      </Box>
    </Box>
  )
}
